class StackedScroller {
  constructor(element) {
    this.element = element;
    this.items = [...this.element.querySelectorAll('.js-stacked-scroller__item')];
    this.cards = this.items.map(
      (item) => item.querySelector('.stacked-scroller__card') || item
    );

    this.scrolling = false;
    this.scrollingFn = null;
    this.offsetY = 32;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!this.items.length) return;

    this.init();
  }

  init() {
    this.setMetrics();
    this.bindIntersection();
    this.bindResize();
  }

  bindIntersection() {
    if (!('IntersectionObserver' in window) || this.reducedMotion) return;

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        if (!entries[0]) return;

        if (entries[0].isIntersecting) {
          if (!this.scrollingFn) {
            this.scrollingFn = this.onScroll.bind(this);
            window.addEventListener('scroll', this.scrollingFn, { passive: true });
            this.onScroll();
          }
        } else if (this.scrollingFn) {
          window.removeEventListener('scroll', this.scrollingFn);
          this.scrollingFn = null;
        }
      },
      { threshold: [0, 1] }
    );

    this.intersectionObserver.observe(this.element);
  }

  bindResize() {
    window.addEventListener('resize', () => {
      this.setMetrics();
      this.requestTick();
    });
  }

  setMetrics() {
    this.offsetY = 32;
    this.elementHeight = this.element.offsetHeight;

    const firstItemStyles = getComputedStyle(this.items[0]);
    this.cardTop = Math.floor(parseFloat(firstItemStyles.getPropertyValue('top'))) || 0;
    this.cardHeight = this.items[0].offsetHeight;

    const targetWidth = parseFloat(getComputedStyle(this.element).getPropertyValue('--ss-target-width')) || 1114;
    this.minScale = this.element.offsetWidth > 0
      ? Math.min(1, targetWidth / this.element.offsetWidth)
      : 0.85;

    this.element.style.paddingBottom = `${this.offsetY * (this.items.length - 1)}px`;

    this.items.forEach((item, index) => {
      item.style.transform = `translateY(${this.offsetY * index}px)`;
    });

    this.cards.forEach((card) => {
      card.style.transform = '';
    });
  }

  requestTick() {
    if (this.scrolling) return;
    this.scrolling = true;
    window.requestAnimationFrame(() => this.update());
  }

  onScroll() {
    this.requestTick();
  }

  update() {
    this.scrolling = false;

    const sectionTop = this.element.getBoundingClientRect().top;

    for (let i = 0; i < this.items.length; i++) {
      const item = this.items[i];

      const trigger = this.cardTop - sectionTop - i * (this.cardHeight + this.offsetY);

      if (trigger > 0) {
        const scale =
          i === this.items.length - 1
            ? 1
            : Math.max(this.minScale, (this.cardHeight - trigger * 0.05) / this.cardHeight);

        item.style.transform = `translateY(${this.offsetY * i}px)`;
        this.cards[i].style.transform = `scale(${scale})`;
      } else {
        item.style.transform = `translateY(${this.offsetY * i}px)`;
        this.cards[i].style.transform = '';
      }
    }
  }

  destroy() {
    if (this.scrollingFn) {
      window.removeEventListener('scroll', this.scrollingFn);
    }

    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
  }
}

document
  .querySelectorAll('.js-stacked-scroller')
  .forEach((element) => new StackedScroller(element));

export default StackedScroller;